import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { createContext, useMemo, useState } from 'react';
import { DELETE_TAG, QUERY_ADMIN_TAGS, UPSERT_TAGS } from './graphql';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const [notification, setNotification] = useState({});

    const { data: tagsData, loading } = useQuery(QUERY_ADMIN_TAGS, {
        variables: {},
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Error querying tags: ${e.message}`,
            });
        },
    });

    const tags = useMemo(() => {
        return tagsData?.results || [].sort((a, b) => a.tag.localeCompare(b.tag));
    }, [tagsData]);

    const [upsertTags] = useMutation(UPSERT_TAGS, {
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Error upserting tags: ${e.message}`,
            });
        },
        update: (cache, { data: { created } }) => {
            cache.updateQuery(
                {
                    query: QUERY_ADMIN_TAGS,
                    variables: {},
                },
                (data) => {
                    const newTags = Object.fromEntries(created.returning.map((tag) => [tag.tag_id, true]));
                    return {
                        ...data,
                        results: [...(data.results || []).filter((tag) => !newTags[tag.tag_id]), ...created.returning],
                    };
                }
            );
        },
    });

    const [deleteTag] = useMutation(DELETE_TAG, {
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({
                severity: 'error',
                message: `Error deleting tag: ${e.message}`,
            });
        },
        update: (cache, { data: { removed } }) => {
            cache.updateQuery(
                {
                    query: QUERY_ADMIN_TAGS,
                    variables: {},
                },
                (data) => {
                    const removedTags = Object.fromEntries(removed.returning.map((tag) => [tag.tag_id, true]));
                    return {
                        ...data,
                        results: [...(data.results || []).filter((tag) => !removedTags[tag.tag_id])],
                    };
                }
            );
        },
    });

    return (
        <Context.Provider
            value={{
                state: {
                    tags,
                    notification,
                },
                loading,
                callbacks: {
                    setNotification,
                    createTag: () =>
                        upsertTags({
                            variables: {
                                tags: [{ client_id: null, accessorial_tag: true, tag: `Tag ${tags.length || 0}` }],
                            },
                        }),
                    updateTag: ({ __typename, always_apply_clients, ...tag }) =>
                        upsertTags({ variables: { tags: [tag] } }),
                    removeTag: (tag) => deleteTag({ variables: { tag_id: tag.tag_id } }),
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
